import AddIcons from '@/icons/AddIcons'
import { getWorkDays } from '@/modules/Dashboard/services'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Holiday } from '@/modules/Holiday/types/response'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import 'dayjs/locale/vi'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Tooltip, Typogaphy } from 'vnetwork-ui'
import { Calendar } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

dayjs.extend(isoWeek)
dayjs.locale('vi')

// Inject gradient border animation CSS
const injectGradientBorderStyles = () => {
    const styleId = 'gradient-border-animation'
    if (document.getElementById(styleId)) return

    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
        @keyframes gradient-border {
            0% { background-position: 0% 50%; }
            100% { background-position: 100% 50%; }
        }
    `
    document.head.appendChild(style)
}

// Inject on module load
injectGradientBorderStyles()

interface WorkDaysProps {
    showCreateButton?: boolean
}

// Format day of week in Vietnamese short form
const formatDayOfWeek = (date: dayjs.Dayjs): string => {
    const day = date.day()
    const dayNames: Record<number, string> = {
        0: 'CN',
        1: 'T2',
        2: 'T3',
        3: 'T4',
        4: 'T5',
        5: 'T6',
        6: 'T7',
    }
    return dayNames[day] || ''
}

// Check if date is in current week
const isCurrentWeek = (date: dayjs.Dayjs, today: dayjs.Dayjs): boolean => {
    const startOfWeek = today.startOf('isoWeek')
    const endOfWeek = today.endOf('isoWeek')
    return (
        (date.isAfter(startOfWeek) || date.isSame(startOfWeek, 'day')) &&
        (date.isBefore(endOfWeek) || date.isSame(endOfWeek, 'day'))
    )
}

// Check if it's a remote work day
const isRemoteDay = (name: string): boolean => {
    return name.toLowerCase().includes('remote')
}

interface WorkDayItemProps {
    holiday: Holiday
    isPast: boolean
    isThisWeek: boolean
}

const WorkDayItem: React.FC<WorkDayItemProps> = ({
    holiday,
    isPast,
    isThisWeek,
}) => {
    const date = dayjs(holiday.holiday_date)
    const dayOfWeek = formatDayOfWeek(date)
    const dateStr = date.format('DD/MM')
    const isRemote = isRemoteDay(holiday.holiday_name)

    // Chip colors based on type and week
    const chipStyles = isPast
        ? 'bg-gray-100 text-gray-500 border-gray-200'
        : isThisWeek
          ? isRemote
              ? 'bg-blue-100 text-blue-800 border-blue-300 font-semibold'
              : 'bg-green-100 text-green-800 border-green-300 font-semibold'
          : isRemote
            ? 'bg-blue-50 text-blue-700 border-blue-200'
            : 'bg-green-50 text-green-700 border-green-200'

    return (
        <div
            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                isPast
                    ? 'opacity-60'
                    : isThisWeek
                      ? 'relative bg-white shadow-md'
                      : 'hover:bg-gray-50'
            }`}
            style={
                isThisWeek && !isPast
                    ? {
                          background:
                              'linear-gradient(white, white) padding-box, linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899, #f59e0b, #10b981, #3b82f6) border-box',
                          border: '2px solid transparent',
                          backgroundSize: '200% 100%',
                          animation: 'gradient-border 8s ease-in-out infinite',
                      }
                    : undefined
            }
        >
            {/* Date block */}
            <div
                className={`flex flex-col items-center justify-center min-w-[50px] py-1 px-2 rounded ${
                    isPast
                        ? 'bg-gray-200 text-gray-500'
                        : isThisWeek
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700'
                }`}
            >
                <span className="text-[10px] font-medium">{dayOfWeek}</span>
                <span className="text-sm font-bold">{dateStr}</span>
            </div>

            {/* Content - Chip */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
                <span
                    className={`text-sm px-3 py-1 rounded-full border font-medium truncate ${chipStyles}`}
                >
                    {holiday.holiday_name}
                </span>
            </div>

            {/* This week badge */}
            {isThisWeek && !isPast && (
                <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                    Tuần này
                </span>
            )}
        </div>
    )
}

const WorkDaysSkeleton = () => (
    <div className="flex-1 space-y-2">
        {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
        ))}
    </div>
)

const WorkDays: React.FC<WorkDaysProps> = ({ showCreateButton = false }) => {
    const today = dayjs()
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()

    const { data, isLoading } = useQueryWithHandler(
        {
            queryKey: ['work-days', today.year(), today.month() + 1],
            queryFn: async () => {
                const response = await getWorkDays(
                    today.year(),
                    today.month() + 1
                )
                return response?.results || []
            },
            staleTime: 5 * 60 * 1000,
        },
        { showLoading: false, showError: false }
    )

    // Sort and categorize holidays
    const sortedHolidays = useMemo(() => {
        if (!data) return []
        return [...data].sort(
            (a, b) =>
                dayjs(a.holiday_date).valueOf() -
                dayjs(b.holiday_date).valueOf()
        )
    }, [data])

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    <Typogaphy className="font-semibold text-primary">
                        {t('work_days.title')}
                    </Typogaphy>
                </div>
                {showCreateButton && (
                    <Tooltip title={t('work_days.add_holiday', 'Thêm ngày lễ')}>
                        <div
                            role="button"
                            data-tour="workday-add-holiday"
                            onClick={() => {
                                navigate(
                                    generatePathDatacentral(
                                        paths.workdayHolidayCreate,
                                        {}
                                    )
                                )
                            }}
                            className="flex cursor-pointer hover:bg-gray-100 rounded p-1 transition-colors"
                        >
                            <AddIcons className="text-primary icon-medium" />
                        </div>
                    </Tooltip>
                )}
            </div>

            {/* Content */}
            {isLoading ? (
                <WorkDaysSkeleton />
            ) : sortedHolidays.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
                    {t('work_days.no_data', 'Không có lịch trong tháng này')}
                </div>
            ) : (
                <div className="flex-1 space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    {sortedHolidays.map((holiday) => {
                        const holidayDate = dayjs(holiday.holiday_date)
                        const isPast = holidayDate.isBefore(today, 'day')
                        const isThisWeek = isCurrentWeek(holidayDate, today)

                        return (
                            <WorkDayItem
                                key={holiday.holiday_id}
                                holiday={holiday}
                                isPast={isPast}
                                isThisWeek={isThisWeek}
                            />
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export default WorkDays
